import { useEffect, useRef } from "react";
import { useAuth } from "@/components/AuthProvider";
import { useToast } from "@/hooks/use-toast";
import { triggerDailyLoginCheck } from "@/services/card-service";
import { checkDailyUsageLimit, incrementDailyUsage } from "@/services/daily-usage-service";

/**
 * Triggers the daily login card drop once per session when a user opens the app.
 * Shows a toast summary if any cards were awarded.
 */
export function useDailyLoginPack(enabled: boolean = true) {
  const { user } = useAuth();
  const { toast } = useToast();
  const triggeredRef = useRef(false);

  useEffect(() => {
    if (!user?.id) return;
    if (!enabled) return;
    if (triggeredRef.current) return;
    triggeredRef.current = true;

    (async () => {
      try {
        const usage = await checkDailyUsageLimit(user.id, "daily_login_pack", 1);
        if (!usage.canUse) {
          return;
        }

        const result = await triggerDailyLoginCheck(user.id);
        const count = result?.cards?.length || 0;

        if (count > 0) {
          // Mark as used so refresh won't trigger again today
          await incrementDailyUsage(user.id, "daily_login_pack", 1);

          const rare = result.cards.filter((c) => ["epic", "legendary"].includes(String(c.rarity)));
          toast({
            title: "Card pack awarded!",
            description: rare.length > 0
              ? `You received ${count} cards • ${rare.length} rare+`
              : `You received ${count} cards`,
          });
        }
      } catch (e) {
        // Silent fail to avoid interrupting user flow
        console.warn("Daily login pack check failed", e);
      }
    })();
  }, [user?.id, toast, enabled]);
}
